import React, { useState, useEffect } from 'react';
import { Coins, AlertTriangle, ShoppingBag, Package, PlusCircle, Clock } from 'lucide-react';
import { User, TokenTransaction } from '../types';
import { 
  getUserTokens, 
  hasEnoughTokens, 
  LOW_TOKENS_THRESHOLD,
  getUserTokenTransactions,
  isUserAdmin
} from '../services/tokenService';
import TokenPurchase from './TokenPurchase';
import { useLanguage } from '../contexts/LanguageContext';

interface TokenStatusProps {
  user: User;
  showPurchaseOptions?: boolean;
}

const TokenStatus: React.FC<TokenStatusProps> = ({ user, showPurchaseOptions = false }) => {
  const [tokens, setTokens] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasEnough, setHasEnough] = useState(true);
  const [showPurchase, setShowPurchase] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'used' | 'acquired'>('all');
  const { t } = useLanguage();

  useEffect(() => {
    // Verify if current user is administrator
    const checkAdmin = async () => {
      if (!user || !user.uid) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log(`TokenStatus: Admin check for user ${user.uid}`);
        // Check if user is admin first
        const adminStatus = await isUserAdmin(user.uid);
        console.log(`TokenStatus: Admin status for ${user.uid} = ${adminStatus}`);
        setIsAdmin(adminStatus);
        
        if (adminStatus) {
          // Admins have unlimited tokens
          setTokens(999999);
          setHasEnough(true);
          setLoading(false);
          return;
        }
        
        // Load tokens for regular users
        const userTokens = await getUserTokens(user.uid);
        setTokens(userTokens);
        
        // Verify if user has enough tokens
        const enough = await hasEnoughTokens(user.uid);
        setHasEnough(enough);
        
        setLoading(false);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('No se pudo verificar el estado del usuario');
        setLoading(false);
      }
    };

    checkAdmin();
  }, [user]);

  // Load transaction history when needed
  useEffect(() => {
    const loadTransactions = async () => {
      if (!showHistory || !user.uid) return;
      
      setLoadingTransactions(true);
      try {
        const history = await getUserTokenTransactions(user.uid);
        setTransactions(history);
      } catch (err) {
        console.warn('Error loading transaction history:', err);
        // Don't set error here, just handle the component gracefully
      } finally {
        setLoadingTransactions(false);
      }
    };
    
    if (showHistory) {
      loadTransactions();
    }
  }, [user.uid, showHistory]);

  // Función para formatear la fecha
  const formatDate = (date: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return 'Fecha desconocida';
    }
    
    try {
      return new Intl.DateTimeFormat('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Error en formato de fecha';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center text-gray-500 py-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
        <span className="text-sm">{t('tokens.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">
        {error}
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div className="flex items-center text-primary bg-blue-50 px-3 py-2 rounded-md">
        <Coins className="h-5 w-5 mr-2 flex-shrink-0" />
        <span className="font-semibold">{t('tokens.adminAccess')}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <div 
        className={`flex flex-col sm:flex-row items-start sm:items-center justify-between ${
          hasEnough 
            ? tokens && tokens <= LOW_TOKENS_THRESHOLD 
              ? 'bg-amber-50 text-amber-700 border border-amber-200' 
              : 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        } px-4 py-3 rounded-lg`}
      >
        <div className="flex items-center mb-3 sm:mb-0">
          {hasEnough ? (
            <Coins className="h-5 w-5 mr-2 flex-shrink-0" />
          ) : (
            <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
          )}
          
          <div>
            <div className="font-medium">
              {tokens === null ? t('tokens.noAvailable') : (
                <>
                  {tokens} {tokens === 1 ? t('tokens.token') : t('tokens.available')}
                </>
              )}
            </div>
            
            {!hasEnough && (
              <p className="text-xs mt-1">
                {t('tokens.noTokens')}
              </p>
            )}
            
            {hasEnough && tokens !== null && tokens <= LOW_TOKENS_THRESHOLD && (
              <p className="text-xs mt-1">
                {t('tokens.lowTokens')}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex space-x-2 w-full sm:w-auto">
          {showPurchaseOptions && (
            <button
              onClick={() => setShowPurchase(!showPurchase)}
              className="flex items-center justify-center text-xs bg-primary text-white px-3 py-1.5 rounded hover:bg-primary-dark transition-colors flex-1 sm:flex-initial"
            >
              <ShoppingBag className="h-3 w-3 mr-1 flex-shrink-0" />
              {t('tokens.buy')}
            </button>
          )}
          
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-center text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-200 transition-colors flex-1 sm:flex-initial"
          >
            <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
            {/* Here's the fix - using 'tokens.history' key directly */}
            {t('tokens.history')}
          </button>
        </div>
      </div>
      
      {showPurchase && (
        <div className="mt-4">
          <TokenPurchase 
            userId={user.uid} 
            onPurchaseComplete={(success) => {
              if (success) {
                // Recargar tokens después de la compra
                getUserTokens(user.uid).then(setTokens);
                setShowPurchase(false);
              }
            }} 
          />
        </div>
      )}
      
      {showHistory && (
        <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-3 bg-gray-50 border-b border-gray-200">
            <h3 className="font-medium text-gray-700">{t('tokens.transactionHistory.title')}</h3>
          </div>
          
          {/* Filter Buttons */}
          <div className="p-3 flex justify-center space-x-2 bg-gray-100 border-b border-gray-200">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-3 py-1 rounded-md text-xs font-medium ${
                activeFilter === 'all' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {t('tokens.transactionHistory.filterAll')}
            </button>
            <button
              onClick={() => setActiveFilter('used')}
              className={`px-3 py-1 rounded-md text-xs font-medium ${
                activeFilter === 'used' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {t('tokens.transactionHistory.filterUsed')}
            </button>
            <button
              onClick={() => setActiveFilter('acquired')}
              className={`px-3 py-1 rounded-md text-xs font-medium ${
                activeFilter === 'acquired' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {t('tokens.transactionHistory.filterAcquired')}
            </button>
          </div>

          {loadingTransactions ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto mb-2"></div>
              {t('tokens.transactionHistory.loading')}
            </div>
          ) : transactions.length > 0 ? (
            <div className="max-h-96 overflow-y-auto">
              {(() => {
                // Filter transactions into two categories
                const usedTransactions = transactions.filter(t => t.type === 'used');
                const acquiredTransactions = transactions.filter(t => t.type === 'purchased' || t.type === 'added' || t.type === 'initial');
                
                return (
                  <div className="divide-y divide-gray-200">
                    {/* Used Quotations Section */}
                    {(activeFilter === 'all' || activeFilter === 'used') && usedTransactions.length > 0 && (
                      <div>
                        <div className="p-3 bg-red-50 border-b border-red-100">
                          <h4 className="font-medium text-red-800 text-sm">{t('tokens.transactionHistory.usedQuotationsSectionTitle')}</h4>
                        </div>
                        {usedTransactions.map((transaction) => (
                          <div key={transaction.id} className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between hover:bg-gray-50">
                            <div className="mb-2 sm:mb-0">
                              <div className="flex items-center">
                                <span className="text-red-500 font-medium">{transaction.amount}</span>
                                <span className="ml-2 text-sm">{transaction.description || 'Sin descripción'}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {formatDate(transaction.createdAt)}
                              </div>
                            </div>
                            
                            <div className="text-xs px-2 py-1 rounded bg-red-100 text-red-800">
                              {t('history.transactionTypes.used')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Purchased/Acquired Credits Section */}
                    {(activeFilter === 'all' || activeFilter === 'acquired') && acquiredTransactions.length > 0 && (
                      <div>
                        <div className="p-3 bg-green-50 border-b border-green-100">
                          <h4 className="font-medium text-green-800 text-sm">{t('tokens.transactionHistory.purchasedCreditsSectionTitle')}</h4>
                        </div>
                        {acquiredTransactions.map((transaction) => (
                          <div key={transaction.id} className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between hover:bg-gray-50">
                            <div className="mb-2 sm:mb-0">
                              <div className="flex items-center">
                                <span className="text-green-500 font-medium">+{transaction.amount}</span>
                                <span className="ml-2 text-sm">{transaction.description || 'Sin descripción'}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {formatDate(transaction.createdAt)}
                              </div>
                            </div>
                            
                            <div className={`text-xs px-2 py-1 rounded ${
                              transaction.type === 'purchased'
                                ? 'bg-purple-100 text-purple-800'
                                : transaction.type === 'initial'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-green-100 text-green-800'
                            }`}>
                              {transaction.type === 'purchased' ? t('history.transactionTypes.purchased') :
                               t('history.transactionTypes.added')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Show message if no transactions in either category */}
                    {usedTransactions.length === 0 && acquiredTransactions.length === 0 && (
                      <div className="p-4 text-center text-gray-500">
                        {t('tokens.transactionHistory.noTransactions')}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              {t('tokens.transactionHistory.noTransactions')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TokenStatus;